import * as uniqBy from 'lodash/uniqBy';

import { TerminalProcedure } from '../../airport/models/terminal-procedure.model';


export class TreeNode<T> {
  private _updated = false;
  private _selected: boolean = null;
  private _docs: number = null;
  private _selectedDocs: number = null;
  private _size: number = null;
  private _selectedSize: number = null;
  private _descendantFiles: Set<string> = null;
  private _descendantProcs: Array<TerminalProcedure> = null;

  get selected(): boolean {
    return this._selected;
  }

  get docs(): number {
    return this._docs;
  }

  get selectedDocs(): number {
    return this._selectedDocs;
  }

  get size(): number {
    return this._size;
  }

  get selectedSize(): number {
    return this._selectedSize;
  }

  constructor(
    public data: T,
    public label: string,
    public children: Array<TreeNode<any>> = [],
    public leaf = false,
    public expanded = false
  ) {
  }

  iterate(fn: (TreeNode) => void) {
    fn(this);
    if (!this.leaf && this.children && this.children.length) {
      this.children.forEach(c => c.iterate(fn));
    }
  }

  iterateLeaves(fn: (TreeNode) => void) {
    if (this.leaf) {
      fn(this);
    } else {
      (this.children || []).forEach(c => c.iterateLeaves(fn));
    }
  }

  updateTree(selectedFiles: Set<string>) {
    this._resetTree();
    this._updateTree(selectedFiles);
  }

  private _resetTree() {
    this.iterate(node => {
      node._updated = false;
      node._descendantFiles = null;
      node._descendantProcs = null;
    });
  }

  private _updateTree(selectedFiles: Set<string>) {
    if (this._updated) {
      return;
    }
    if (this.leaf) {
      if (this.data !== null && this.data instanceof TerminalProcedure) {
        this._selected = selectedFiles.has(this.data.fullFilename);
        this._docs = 1;
        this._size = this.data.filesize;
        this._selectedDocs = this._selected ? 1 : 0;
        this._selectedSize = this._selected ? this._size : 0;
      } else {
        this._selected = false;
        this._docs = 0;
        this._size = 0;
        this._selectedDocs = 0;
        this._selectedSize = 0;
      }
    } else {
      this.children.forEach(child => child._updateTree(selectedFiles));
      this._selected = this.children.every(c => c._selected)
        ? true
        : (this.children.some(c => c._selected) ? null : false);
      this._docs = this.children.reduce((prev, curr) => prev + curr._docs, 0);
      this._size = this.children.reduce((prev, curr) => prev + curr._size, 0);
      const selectedProcs = this.getUniqueDescendantProcs()
        .filter(p => selectedFiles.has(p.fullFilename));
      this._selectedDocs = selectedProcs.length;
      this._selectedSize = selectedProcs.reduce((prev, curr) => prev + curr.filesize, 0);
    }
    this._updated = true;
  }

  getDescendantFiles(): Set<string> {
    if (this._descendantFiles) {
      return this._descendantFiles;
    }
    if (this.leaf) {
      if (this.data !== null && this.data instanceof TerminalProcedure) {
        return this._descendantFiles = new Set<string>([this.data.fullFilename]);
      }
    } else {
      this._descendantFiles = new Set<string>();
      for (const child of this.children) {
        child.getDescendantFiles().forEach(proc => {
          this._descendantFiles.add(proc);
        });
      }
      return this._descendantFiles;
    }
    return this._descendantFiles = new Set<string>();
  }

  getDescendantProcs(selected = false): Array<TerminalProcedure> {
    if (this.leaf) {
      if (this.data !== null && this.data instanceof TerminalProcedure) {
        return this._descendantProcs = (this._selected || !selected) ? [this.data] : [];
      }
    } else {
      return this._descendantProcs = [].concat(...(this.children || []).map(c => c.getDescendantProcs(selected)));
    }
    return this._descendantProcs = [];
  }

  getUniqueDescendantProcs(): Array<TerminalProcedure> {
    return uniqBy(this.getDescendantProcs(), (proc: TerminalProcedure) => proc.fullFilename);
  }

  getSelectedProcs(): Array<TerminalProcedure> {
    return this.getDescendantProcs(true);
  }
}
